import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, message, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CloseCircleOutlined } from '@ant-design/icons';
import { setAnalyticsFilterOptions } from '../../redux/analytics/actions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../data/base';

const { Option } = Select;

/**
 * A component for selecting products based on selected catalogs.
 */
const SimpleProductSelect = React.forwardRef(({ onChange, selectedValue, setSelectedValue, loadingSelect, setLoadingSelect, selectedCatalogs = [] }, ref) => {
    const [loading, setLoading] = useState(false);
    // Stato che mantiene tutti i prodotti caricati da CataloguesProducts
    const [allCatalogueProducts, setAllCatalogueProducts] = useState([]);
    // Stato che mantiene i prodotti filtrati per catalogo
    const [filteredProducts, setFilteredProducts] = useState([]);
    // Stato che mantiene i prodotti selezionati (solo localmente)
    const [selectedProducts, setSelectedProducts] = useState([]);
    const dispatch = useDispatch();

    // Ottieni le opzioni di filtro attuali da Redux
    const filterOptions = useSelector(state => state.analytics.filterOptions || {});

    // Carica tutti i prodotti da CataloguesProducts una sola volta
    useEffect(() => {
        const fetchAllCatalogueProducts = async () => {
            setLoading(true);
            if (setLoadingSelect) setLoadingSelect(true);

            try {
                const productsQuery = query(
                    collection(db, "CataloguesProducts"),
                    where("status", "==", "Pubblicato")
                );

                const querySnapshot = await getDocs(productsQuery);
                const products = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    products.push({
                        id: doc.id,
                        nome_modello: data.modelName || data.nome_modello || 'Unknown Model',
                        catalogRef: data.catalogRef,
                        ...data
                    });
                });

                setAllCatalogueProducts(products);
            } catch (error) {
                console.error('Error fetching catalogue products:', error);
                message.error('Error loading products');
                setAllCatalogueProducts([]);
            } finally {
                setLoading(false);
                if (setLoadingSelect) setLoadingSelect(false);
            }
        };

        fetchAllCatalogueProducts();
    }, []); // Carica solo una volta

    // Filtra i prodotti in base ai cataloghi selezionati
    useEffect(() => {
        if (allCatalogueProducts.length === 0) {
            setFilteredProducts([]);
            return;
        }

        // Determina quali cataloghi utilizzare per il filtro
        const isAllCatalogSelected = selectedCatalogs.some(cat => cat.id === 'all');

        let productsToShow = [];

        if (isAllCatalogSelected) {
            // Se "all" è selezionato, mostra tutti i prodotti
            productsToShow = [...allCatalogueProducts];
        } else {
            // Altrimenti filtra per i cataloghi selezionati
            const selectedCatalogIds = selectedCatalogs
                .filter(cat => cat && cat.id && cat.id !== 'all')
                .map(cat => cat.id);

            if (selectedCatalogIds.length > 0) {
                productsToShow = allCatalogueProducts.filter(product =>
                    selectedCatalogIds.includes(product.catalogRef)
                );
            }
        }

        // Raggruppa i prodotti per nome modello
        const groupedByModelName = {};

        productsToShow.forEach(product => {
            const modelName = product.nome_modello || `Product ${product.id.substring(0, 5)}...`;

            if (!groupedByModelName[modelName]) {
                groupedByModelName[modelName] = {
                    modelName,
                    ids: [],
                    representativeProduct: product
                };
            }

            groupedByModelName[modelName].ids.push(product.id);
        });

        // Converte l'oggetto in array e ordina
        const groupedProducts = Object.values(groupedByModelName);
        groupedProducts.sort((a, b) => a.modelName.localeCompare(b.modelName));

        setFilteredProducts(groupedProducts);
    }, [allCatalogueProducts, selectedCatalogs]);

    // Effetto per sincronizzare la selezione con il filtro 'glass' in Redux
    useEffect(() => {
        // Se c'è un valore 'glass' nei filtri ma non è selezionato, troviamo il gruppo corrispondente
        if (filterOptions.glass && selectedProducts.length === 0) {
            // Cerca in tutti i gruppi di prodotti
            for (const group of filteredProducts) {
                // Se uno degli ID nel gruppo corrisponde al filtro 'glass'
                if (group.ids.some(id => String(id) === String(filterOptions.glass))) {
                    // Aggiorna la selezione
                    setSelectedProducts([group.modelName]);

                    if (setSelectedValue) {
                        setSelectedValue(group.modelName);
                    }

                    // Abbiamo trovato una corrispondenza, usciamo dal ciclo
                    break;
                }
            }
        }
    }, [filterOptions.glass, filteredProducts, selectedProducts, setSelectedValue]);

    // Gestisce la modifica della selezione dei prodotti - SOLO LOCALMENTE
    const handleChange = (values) => {
        // Aggiorna la selezione locale
        setSelectedProducts(values);

        // Raccoglie le informazioni sui prodotti selezionati
        const allSelectedIds = [];
        const allSelectedDisplayNames = [];

        values.forEach(modelName => {
            const selectedGroup = filteredProducts.find(group => group.modelName === modelName);
            if (selectedGroup) {
                // Assicurati di salvare solo stringhe ID, non riferimenti agli oggetti
                const safeIds = selectedGroup.ids.map(id => {
                    // Se id è un oggetto DocumentReference, prendi solo l'id
                    if (typeof id === 'object' && id !== null) {
                        return id.id || String(id);
                    }
                    return String(id);
                });

                allSelectedIds.push(...safeIds);
                allSelectedDisplayNames.push(selectedGroup.modelName);
            }
        });

        // Chiama il callback onChange per notificare i componenti genitori (Filters.js)
        // Questo non modifica ancora Redux, solo lo stato locale
        if (onChange) {
            onChange({
                ids: allSelectedIds,
                displayNames: allSelectedDisplayNames
            });
        }

        // Aggiorniamo selectedValue per retrocompatibilità
        if (setSelectedValue && values.length > 0) {
            setSelectedValue(values[values.length - 1]);
        } else if (setSelectedValue) {
            setSelectedValue('');
        }
    };

    // Applica i filtri a Redux - Deve essere chiamato quando si preme "Apply Filters" in Filters.js
    const applyFilters = useCallback((ids) => {
        if (ids && ids.length > 0) {
            const updatedFilterOptions = {
                ...filterOptions,
                glass: ids[0] // Prendiamo solo il primo per retrocompatibilità
            };

            // Dispatch delle opzioni filtro aggiornate a Redux
            dispatch(setAnalyticsFilterOptions(updatedFilterOptions));
        } else {
            // Se non ci sono ID, cancella i filtri
            dispatch(setAnalyticsFilterOptions({
                ...filterOptions,
                glass: '',
                variant: '' // Reset anche della variant perché dipende da glass
            }));
        }
    }, [dispatch, filterOptions]);

    // Reset selection
    const resetSelection = useCallback(() => {
        setSelectedProducts([]);

        // Notifica il componente genitore
        if (onChange) {
            onChange({
                ids: [],
                displayNames: []
            });
        }

        // Aggiorna selectedValue per retrocompatibilità
        if (setSelectedValue) {
            setSelectedValue('');
        }
    }, [onChange, setSelectedValue]);

    // Funzione pubblica per abilitare il reset dall'esterno
    React.useImperativeHandle(
        ref,
        () => ({
            resetSelection,
            applyFilters
        }),
        [resetSelection, applyFilters]
    );

    const getTagPlaceholder = () => {
        if (selectedProducts.length === 1 && selectedProducts[0] === 'all') {
            return 'All Products';
        }
        if (selectedProducts.length > 0) {
            return `${selectedProducts.length} products selected`;
        }
        return null;
    };

    // Render a simplified tag
    const SimpleTag = ({ label }) => (
        <span style={{
            padding: '0 7px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e8e8e8',
            borderRadius: '2px',
            marginRight: 4,
            display: 'inline-block',
            fontSize: 12,
            lineHeight: '20px',
            height: 22
        }}>
            {label}
        </span>
    );

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <Select
                mode="multiple"
                placeholder="Select a product"
                loading={loading}
                value={selectedProducts}
                onChange={handleChange}
                maxTagCount={selectedProducts.length === 1 && selectedProducts[0] === 'all' ? 1 : 0}
                maxTagPlaceholder={getTagPlaceholder()}
                removeIcon={<span />} // Rimuove l'icona X dai tag
                tagRender={(props) => <SimpleTag label={props.label} />}
                style={{
                    width: '100%'
                }}
                notFoundContent={loading ? <Spin size="small" /> : "No products found"}
                optionFilterProp="children"
            >
                {filteredProducts.map(group => (
                    <Option key={group.modelName} value={group.modelName}>
                        {group.modelName} ({group.ids.length})
                    </Option>
                ))}
            </Select>
            {selectedProducts.length > 0 && !(selectedProducts.length === 1 && selectedProducts[0] === 'all') && (
                <Button
                    type="text"
                    icon={<CloseCircleOutlined style={{ color: "grey" }} />}
                    onClick={() => handleChange([])}
                    style={{
                        position: "absolute",
                        top: "50%",
                        right: "25px",
                        transform: "translateY(-50%)",
                        zIndex: 1,
                    }}
                />
            )}
        </div>
    );
});

export default SimpleProductSelect;